import React, { useCallback, useEffect, useState } from "react";
import { Autocomplete, Box, Button, Chip, CircularProgress, FormControl, TextField, Typography } from "@mui/material";
import { useDialog } from "../../../DialogContext";
import { useDispatch } from "react-redux";
import { debounce, generateUniqueMatterName, pageScrollStyle } from "../../../../styles/styles";
import { getActivities } from "../../../../redux/Apps/Lawyerapp/ActivitySlice/ActivitySlice";
import ServiceTimeForm from "../../Activities/ActivitiesForms/ServiceTimeForm";
import ExpenseForm from "../../Activities/ActivitiesForms/ExpenseForm";import noOption from "../../../../assets/no-option-found.webp";
import { AccessTimeRounded, AttachMoneyRounded } from "@mui/icons-material";
import { Controller } from "react-hook-form";

export const ActivityApiField = ({
  name,
  label = "Activity",
  control,
  value,
  onChange,
  errors,
  token,
  apiUrl,
  isRequired = false,
  renderNoOptions,
  formatApiResponse,
  tabName,
  editData = null,
  multiple = true,
  placeholder = "Select activity",
  returnFullData = false,
  disabled = false,
  refreshKey = 0,
  searchKey = "search",
}) => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const [apiOptions, setApiOptions] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [nextUrl, setNextUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchActivities(searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, apiUrl, token]);

  const debouncedSearch = useCallback(
    debounce(async (query) => {
      await fetchActivities(query);
    }, 300),
    [apiUrl, token, formatApiResponse, tabName]
  );

  const getNextPage = (url) => {
    if (!url) return 1;
    const u = new URL(url);
    return parseInt(u.searchParams.get("page")) || 1;
  };

  const fetchActivities = async (searchQuery = "", isLoadMore = false) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const result = await dispatch(
        getActivities({
          url: apiUrl,
          token,
          page: isLoadMore ? getNextPage(nextUrl) : 1,
          search: searchQuery,
          searchKey,
        })
      ).unwrap();

      const resultsData = result?.results || [];

      const formatted = formatApiResponse
        ? formatApiResponse({ res: resultsData, tabName, api: apiUrl })
        : resultsData;

      const safeFormatted = Array.isArray(formatted) ? formatted : [];

      if (isLoadMore) {
        setApiOptions((prev) => [...(prev || []), ...safeFormatted]);
      } else {
        const combinedOptions = [...safeFormatted];
        if (
          editData &&
          !combinedOptions.find((option) => option.id === editData.id)
        ) {
          combinedOptions.unshift(editData);
        }
        setApiOptions(combinedOptions);
      }

      const paginationData = result?.pagination || {};
      setNextUrl(paginationData.next || null);
    } catch (err) {
      console.error("Fetch activities error:", err);
      setApiOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (value && apiOptions.length > 0) {
      const selected = multiple
        ? (value || [])
            .map((v) =>
              typeof v === "object" ? v : apiOptions.find((o) => o.id === v)
            )
            .filter(Boolean)
        : typeof value === "object"
        ? value
        : apiOptions.find((o) => o.id === value) || null;
      setSelectedActivities(selected);
    } else if (!value) {
      setSelectedActivities(multiple ? [] : null);
    }
  }, [value, apiOptions, multiple]);

  const handleInputChange = (_, val) => {
    setSearchText(val);
    debouncedSearch(val);
  };

  const handleSelectionChange = (e, newVal, fieldOnChange) => {
    setSelectedActivities(newVal);
    const val = returnFullData
      ? newVal
      : (newVal || []).map((opt) => opt?.id).filter(Boolean);
    if (typeof fieldOnChange === "function") fieldOnChange(val);
    if (onChange) onChange(val);
  };

  const handleScroll = (e) => {
    const node = e.currentTarget;
    if (
      !isLoading &&
      nextUrl &&
      node.scrollTop + node.clientHeight >= node.scrollHeight - 50
    ) {
      fetchActivities(searchText, true);
    }
  };

  // Fixed button handlers with proper event handling
  const handleAddTimeEntry = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      showDialog(<ServiceTimeForm />, { dialogTitle: "New Time Entry" });
    } catch (error) {
      console.error("Error opening time entry dialog:", error);
    }
  };

  const handleAddExpenseEntry = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      showDialog(<ExpenseForm />, { dialogTitle: "New Expense Entry" });
    } catch (error) {
      console.error("Error opening expense entry dialog:", error);
    }
  };

  const NoOptionContent = (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 1,
        position: "relative",
        zIndex: 1000,
      }}
    >
      <Box sx={{ height: "6rem" }}>
        <img
          src={noOption}
          alt="No Data"
          style={{ height: "100%", width: "100%", objectFit: "contain" }}
          onError={(e) => {
            e.onerror = null;
            e.target.src = require("../../../../assets/no-option-found.webp");
          }}
        />
      </Box>
      <Typography
        variant="body1"
        sx={{ fontSize: "0.9rem", textAlign: "center" }}
      >
        No activities found
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleAddTimeEntry}
          sx={{
            pointerEvents: "auto",
            zIndex: 1001,
          }}
        >
          Add time entry
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleAddExpenseEntry}
          sx={{
            pointerEvents: "auto",
            zIndex: 1001,
          }}
        >
          Add expense entry
        </Button>
      </Box>
    </Box>
  );

  // Fixed ListboxComponent with proper event handling
  const ListboxComponent = React.forwardRef((props, ref) => {
    const { children, ...rest } = props;

    return (
      <Box ref={ref} sx={{ position: "relative" }}>
        <Box
          {...rest}
          sx={{
            ...pageScrollStyle,
            paddingBottom: "60px", // Add padding to prevent content overlap
          }}
        >
          {children}
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            borderTop: "1px solid #e5e5e5",
            backgroundColor: "white",
            p: 1,
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
            zIndex: 9999,
            boxShadow: "0 -2px 4px rgba(0,0,0,0.1)",
          }}
          // Prevent the autocomplete from closing when clicking in this area
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Button
            variant="text"
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddTimeEntry(e);
            }}
            sx={{
              pointerEvents: "auto",
              zIndex: 10000,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Add time entry
          </Button>
          <Button
            variant="text"
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddExpenseEntry(e);
            }}
            sx={{
              pointerEvents: "auto",
              zIndex: 10000,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Add expense entry
          </Button>
        </Box>
      </Box>
    );
  });

  const commonProps = {
    multiple,
    fullWidth: true,
    options: Array.isArray(apiOptions) ? apiOptions : [],
    inputValue: searchText,
    onInputChange: handleInputChange,
    loading: isLoading,
    disabled,
    getOptionLabel: (option) => option?.description || option?.type || "",
    isOptionEqualToValue: (option, value) => option?.id === value?.id,
    ListboxProps: {
      onScroll: handleScroll,
      style: {
        maxHeight: "250px",
        overflowY: "auto",
        position: "relative",
      },
    },
    noOptionsText: renderNoOptions || NoOptionContent,
    renderInput: (params) => (
      <TextField
        {...params}
        label={
          <span>
            {label}
            {isRequired && <span style={{ color: "red" }}> *</span>}
          </span>
        }
        placeholder={placeholder}
        size="small"
        fullWidth
        error={Boolean(errors?.[name])}
        helperText={errors?.[name]?.message}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {isLoading && <CircularProgress color="primary" size={20} />}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    ),
    ListboxComponent,
    disableCloseOnSelect: true,
    // Modified onClose to handle button clicks better
    onClose: (event, reason) => {
      // Don't close if clicking on buttons or within the button container
      if (reason === "blur" || reason === "selectOption") {
        const target = event?.target;
        if (
          target?.closest(".MuiButton-root") ||
          target?.closest("[data-button-container]")
        ) {
          return false;
        }
      }
    },
    renderOption: (props, option) => (
      <li
        {...props}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: "10px 14px",
          borderBottom: "1px solid #eee",
          cursor: "pointer",
          transition: "background 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#f9f9f9")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        {/* Top Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            mb: 0.5,
          }}
        >
          {/* Icon */}
          {option?.type === "time" ? (
            <AccessTimeRounded
              sx={{ fontSize: 16, color: "primary.main" }}
            />
          ) : (
            <AttachMoneyRounded
              sx={{ fontSize: 16, color: "success.main" }}
            />
          )}

          {/* Description */}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              flex: 1,
              fontSize: "0.9rem",
              color: "text.primary",
            }}
          >
            {option?.description || "No description"}
          </Typography>

          {/* Amount */}
          <Chip
            label={`₹${option?.total_payment || "0.00"}`}
            size="small"
            color={option?.type === "time" ? "primary" : "success"}
            variant="outlined"
            sx={{ fontSize: "0.75rem" }}
          />
        </Box>

        {/* Category */}
        {option?.category && (
          <Box sx={{ pl: 3, mb: 0.5 }}>
            <Chip
              label={option.category.name}
              size="small"
              variant="outlined"
              color="default"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          </Box>
        )}

        {/* Middle Row - Details */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pl: 3,
            flexWrap: "wrap",
            color: "text.secondary",
            fontSize: "0.75rem",
          }}
        >
          {option?.type === "time" && option?.time_in && option?.time_out && (
            <Typography variant="caption">
              {new Date(option.time_in).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              -{" "}
              {new Date(option.time_out).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Typography>
          )}

          {option?.type === "expense" && option?.quantity && (
            <Typography variant="caption">Qty: {option.quantity}</Typography>
          )}

          {option?.hourly_rate && (
            <Typography variant="caption">
              Rate: ₹{option.hourly_rate}
              {option?.type === "time" ? "/hr" : ""}
            </Typography>
          )}

          {option?.created_at && (
            <Typography variant="caption">
              {new Date(option.created_at).toLocaleDateString()}
            </Typography>
          )}
        </Box>

        {/* Status Chips */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            pl: 3,
            mt: 0.5,
            flexWrap: "wrap",
          }}
        >
          {option?.non_billable && (
            <Chip
              label="Non-billable"
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
          {option?.is_paid && (
            <Chip label="Paid" size="small" color="success" variant="filled" />
          )}
          {option?.bill_genrated && (
            <Chip label="Billed" size="small" color="info" variant="outlined" />
          )}
          {!option?.bill_entry_show && (
            <Chip label="Hidden from bill" size="small" variant="outlined" />
          )}
        </Box>

        {/* Case / Lawyer Info */}
        {(option?.lawyer || option?.case) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              pl: 3,
              mt: 0.5,
            }}
          >
            {option?.case?.id && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {generateUniqueMatterName(
                  option.case.id,
                  option.case.case_name
                )}
              </Typography>
            )}
          </Box>
        )}
      </li>
    ),
  };

  if (control && name) {
    return (
      <Controller
        name={name}
        control={control}
        rules={isRequired ? { required: `Please fill ${label}` } : {}}
        render={({ field }) => {
          const controlValue = multiple
            ? (field.value || [])
                .map((v) =>
                  typeof v === "object" ? v : apiOptions.find((o) => o.id === v)
                )
                .filter(Boolean)
            : typeof field.value === "object"
            ? field.value
            : apiOptions.find((o) => o.id === field.value) || null;
          return (
            <Autocomplete
              {...commonProps}
              size="small"
              disablePortal={false}
              disableCloseOnSelect
              value={controlValue}
              onChange={(e, newVal) =>
                handleSelectionChange(e, newVal, field.onChange)
              }
              PopperProps={{ container: document.body, sx: { zIndex: 14000 } }}
            />
          );
        }}
      />
    );
  }

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        {...commonProps}
        size="small"
        disablePortal={false}
        disableCloseOnSelect
        value={selectedActivities}
        onChange={handleSelectionChange}
        PopperProps={{ container: document.body, sx: { zIndex: 14000 } }}
      />
    </FormControl>
  );
};
